import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Anime } from '@/mocks/anime';
import AnimeCard from '@/components/base/AnimeCard';

export interface CategoryTab {
  key: string;
  label: string;
}

interface CategoryLayoutProps {
  category: 'healing' | 'daily';
  eyebrow: string;
  title: string;
  subtitle: string;
  items: Anime[];
  tabs: CategoryTab[];
}

export default function CategoryLayout({
  eyebrow,
  title,
  subtitle,
  items,
  tabs,
}: CategoryLayoutProps) {
  const [activeTab, setActiveTab] = useState('all');

  const filtered = useMemo(() => {
    if (activeTab === 'all') return items;
    if (activeTab === '萌系') return items.filter((a) => a.category === 'moe');
    return items.filter((a) => a.tags.includes(activeTab) || a.tags.includes(`${activeTab}心`));
  }, [activeTab, items]);

  return (
    <div className="pt-16">
      <header className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="transition hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{title}</span>
          </nav>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-accent-600">
            <span className="inline-block h-1.5 w-6 rounded-full bg-accent-500"></span>
            {eyebrow}
          </div>
          <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {subtitle}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((anime) => (
            <AnimeCard key={anime.id} anime={anime} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-2xl bg-background-100 py-16 text-center text-sm text-foreground-600">
            这个分类下暂时没有作品，换个标签看看吧。
          </div>
        )}
      </main>
    </div>
  );
}